import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TbFolderOpen, TbDownload } from 'react-icons/tb';
import { sum, groupBy } from 'lodash';
import { round, displayNumber, roundToFixed, unum } from '@oliasoft-open-source/units';
import {
  Table,
  Flex,
  Heading,
  Menu,
  Button,
  toast,
  Portal,
  Spacer,
  Icon,
  Text,
} from '@oliasoft-open-source/react-ui-library';
import ExcelJS from 'exceljs';
import { writeExcelFile } from 'src/common/write-excel-file/write-excel-file';
import { simulationChartType } from 'src/enums/simulations';
import translations from 'src/internationalisation/translation-map.json';
import { selectPrimaryCurrency } from 'store/entities/company-settings/selectors';
import { decimalSeparator, HOURS_PER_DAY } from 'src/enums/general';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { selectCostCodesIdToCodeMap } from 'src/store/entities/cost-codes/selectors';
import { CellWithExpandingIcon } from 'views/time-tracker/cell-with-expanding-icon';
import { selectCompareEstimatesActiveEstimates } from 'store/entities/compare-estimates/selectors';
import { selectProjectOverview } from 'store/entities/projects/selectors';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { downloadAFEReportExcel } from 'src/views/overview/utils/download-afe-report-excel';
import { thousandsSeparators } from 'src/views/cost-tracker/cost-tracker-common/utils/utils';
import {
  convertOperationDurations,
  convertOperationVsDepthDurations,
  shownCumulativeProbabilities,
} from '../simulations';
import './style.less';
import {
  getMean,
  getSimulationDataForWaterfall,
  sectionObjectKeys,
  normalizeValue,
  getNumberValue,
  getCostItemOverviewTotal,
} from './result-utils';

const ResultTable = ({
  visibleEstimates,
  simulationsResult,
  selectedItem,
  selectedDataEntity,
  primaryCurrency,
  controlsPortalId,
  activityModelOperations,
  chartKey,
  riskFilter,
  costCodesIdToCodeMap,
  depthUnit,
  showSections = true,
  projectOverview,
}) => {
  const { t } = useTranslation();
  const [closedIds, setClosedIds] = useState([]);
  const { name, wellbore } = projectOverview;

  let tableData = null;
  const subactivity = simulationsResult?.cumulative_subactivity_percentiles
    ?.filter((_, index) => shownCumulativeProbabilities.includes(index))
    ?.map((el) => el / 24);
  const toggleExpandedRow = (id) =>
    closedIds.includes(id)
      ? setClosedIds(closedIds.filter((existingId) => existingId !== id))
      : setClosedIds([...closedIds, id]);

  switch (selectedItem?.value) {
    case simulationChartType.CUMULATIVE_PROBABILITY:
      {
        const values =
          selectedDataEntity === 0
            ? simulationsResult?.cumulative_probability?.time?.map((t) => t / HOURS_PER_DAY)
            : (simulationsResult?.cumulative_probability_cost?.costs ??
              simulationsResult?.cumulative_probability_cost?.cost);
        tableData = values?.filter((_, index) => shownCumulativeProbabilities.includes(index));
      }
      break;

    case simulationChartType.OPERATION_DURATIONS:
      {
        const labels =
          selectedDataEntity === 0
            ? simulationsResult?.operation_durations?.labels
            : simulationsResult?.operation_costs?.labels;
        const values =
          selectedDataEntity === 0
            ? convertOperationDurations(simulationsResult?.operation_durations?.durations)
            : simulationsResult?.operation_costs?.costs;
        tableData = labels?.map((item, index) => ({
          label: item,
          P10: values['10'][index],
          P50: values['50'][index],
          P90: values['90'][index],
          mean: values.mean[index],
        }));
      }
      break;

    case simulationChartType.SECTION_DURATIONS:
      {
        const labels = simulationsResult?.section_durations?.labels;
        const values = convertOperationDurations(simulationsResult?.section_durations?.durations);
        tableData = labels?.map((item, index) => ({
          label: item,
          P10: values['10'][index],
          P50: values['50'][index],
          P90: values['90'][index],
          mean: values.mean[index],
        }));
      }
      break;

    case simulationChartType.TIME_DEPTH:
      {
        const operations = simulationsResult?.operations?.flatMap((num) => [num, num]);

        const sections = operations?.map((op) => {
          const currentOperation = activityModelOperations.find((operation) => operation.operationId === op.id);
          return {
            projectSectionId: currentOperation?.projectSectionId,
            projectSectionName: currentOperation?.projectSectionName ?? op.name ?? '',
          };
        });
        const operationVsDepth =
          selectedDataEntity === 0
            ? convertOperationVsDepthDurations(simulationsResult?.operation_vs_depth)
            : simulationsResult?.operation_cost_vs_depth;
        tableData = { ...operationVsDepth, operations, sections };
      }
      break;
    case simulationChartType.ACTIVITY_DURATIONS:
      {
        const operations = simulationsResult?.operations;
        tableData = operations?.map((item) => ({
          label: item.name,
          activity: item.actions,
        }));
      }
      break;
    case simulationChartType.COST_ITEM_OVERVIEW:
      tableData = simulationsResult?.cost_results;
      break;
    case simulationChartType.SECTION_WATERFALL:
      tableData =
        selectedDataEntity === 0
          ? sum(simulationsResult?.section_durations?.durations?.mean)
          : sum(simulationsResult?.section_costs?.costs?.mean);
      break;
    case simulationChartType.RISKS: {
      const filterMap = riskFilter.reduce((acc, curr) => ({ ...acc, [curr.value]: curr.selected }), {});

      const datasets = activityModelOperations
        .filter((operation) => filterMap[operation.operationId] && filterMap[operation.projectSectionId])
        .reduce((riskData, operation) => {
          const operationTaskData = operation?.tasks
            .filter((task) => !task.isBranch && task.parentId !== null)
            .map((task) => ({
              name: task.name,
              certainty: task.certainty,
              mean: getMean(task, operation?.tasks),
            }));
          return [...riskData, ...operationTaskData];
        }, []);
      tableData = datasets;

      break;
    }
    case simulationChartType.AFE_REPORT: {
      const sectionCostPerItem = simulationsResult?.section_cost_per_item ?? {};
      const sectionList = Object.keys(sectionCostPerItem).filter((key) => sectionCostPerItem[key].length > 0);
      tableData = {
        costResults: simulationsResult?.cost_results ?? [],
        sectionList,
        sectionDurations: simulationsResult?.section_durations ?? {
          labels: [],
          durations: { mean: [] },
        },
        sectionContributionToCost: simulationsResult?.section_contribution_to_cost_item ?? {},
        sectionCostPerItem,
        sectionCosts: simulationsResult?.section_costs ?? {
          labels: [],
          costs: { mean: [] },
        },
        average: simulationsResult?.average,
        average_cost: simulationsResult?.average_cost,
      };
      break;
    }
    default:
      tableData = [];
      break;
  }

  const generateTableHeaders = (selectedType) => {
    const tableSubHeader = tableSubHeaderName(selectedDataEntity === 0 ? t(translations.days) : primaryCurrency);
    switch (selectedType) {
      case simulationChartType.OPERATION_DURATIONS:
      case simulationChartType.SECTION_DURATIONS:
      case simulationChartType.TIME_DEPTH: {
        const timeDepthHeaders = showSections
          ? [{ value: t(translations.section) }, { value: t(translations.operation) }, { value: t(translations.depth) }]
          : [{ value: t(translations.operation) }, { value: t(translations.depth) }];
        const timeDepthSubHeaders = showSections ? [{ value: '' }, { value: '' }] : [{ value: '' }];

        return [
          {
            cells: [
              ...(selectedType === simulationChartType.TIME_DEPTH
                ? timeDepthHeaders
                : [{ value: t(translations.operation) }]),
              { value: 'Mean' },
              { value: 'P10' },
              { value: 'P50' },
              { value: 'P90' },
            ],
          },
          {
            cells: [
              ...(selectedType === simulationChartType.TIME_DEPTH ? timeDepthSubHeaders : []),
              { value: tableSubHeaderName(depthUnit) },
              { value: tableSubHeader },
              { value: tableSubHeader },
              { value: tableSubHeader },
              { value: tableSubHeader },
            ],
          },
        ];
      }
      case simulationChartType.CUMULATIVE_PROBABILITY:
        return [
          {
            cells: [
              { value: '' },
              ...shownCumulativeProbabilities?.map((item) => ({
                value: `P${item + 1}`,
              })),
            ],
          },
          {
            cells: [
              { value: '' },
              ...shownCumulativeProbabilities?.map(() => ({
                value: tableSubHeader,
              })),
            ],
          },
        ];
      case simulationChartType.ACTIVITY_DURATIONS:
        return [
          {
            cells: [
              { value: t(translations.simulations_activity) },
              { value: t(translations.simulations_mean) },

              { value: 'P10' },
              { value: 'P50' },
              { value: 'P90' },
            ],
          },
          {
            cells: [
              { value: '' },
              { value: tableSubHeaderName(t(translations.hours)) },
              { value: tableSubHeaderName(t(translations.hours)) },
              { value: tableSubHeaderName(t(translations.hours)) },
              { value: tableSubHeaderName(t(translations.hours)) },
            ],
          },
        ];

      case simulationChartType.COST_ITEM_OVERVIEW:
        return [
          {
            cells: [
              { value: t(translations.code) },
              {
                value: t(translations.projects_costItems),
              },
              { value: 'Mean' },
              { value: 'P10' },
              { value: 'P50' },
              { value: 'P90' },
            ],
          },
          {
            cells: [
              { value: '' },
              { value: '' },
              { value: tableSubHeader },
              { value: tableSubHeader },
              { value: tableSubHeader },
              { value: tableSubHeader },
            ],
          },
        ];
      case simulationChartType.RISKS:
        return [
          {
            cells: [
              {
                value: t(translations.risks_risks),
              },
              { value: t(translations.simulations_probabilityOfOccurring) },
              { value: `${t(translations.simulations_meanTime)}` },
            ],
          },
          {
            cells: [
              { value: '' },
              { value: tableSubHeaderName(t(translations.percent)) },
              { value: tableSubHeaderName(t(translations.hours)) },
            ],
          },
        ];
      case simulationChartType.SECTION_WATERFALL: {
        const { dataType, threshold } = getSimulationDataForWaterfall(
          simulationsResult,
          selectedDataEntity,
          HOURS_PER_DAY,
        );
        return [
          {
            cells: [
              { value: `${t(translations.section)}` },
              {
                value:
                  dataType === sectionObjectKeys.DURATIONS
                    ? t(translations.days)
                    : `${t(translations.cost)} ${threshold ? `[M${primaryCurrency}]` : `[${primaryCurrency}]`}`,
              },
            ],
          },
        ];
      }
      case simulationChartType.AFE_REPORT: {
        const {
          sectionList = [],
          sectionDurations = { labels: [], durations: { mean: [] } },
          average,
        } = tableData || {};
        const sectionsMeanDays = sectionList.map((sectionName) => {
          const sectionIdx = sectionDurations.labels.findIndex((s) => s === sectionName);
          return tableSubHeaderName(
            `${round(sectionDurations.durations.mean[sectionIdx] / HOURS_PER_DAY, 1)} ${t(translations.days)}`,
          );
        });
        return [
          {
            cells: [
              { value: t(translations.code) },
              { value: t(translations.simulations_costCategoryItem) },
              { value: t(translations.total) },
              ...sectionList.map((name) => ({ value: name })),
            ],
          },
          {
            cells: [
              { value: '' },
              { value: '' },
              {
                value: tableSubHeaderName(`${round(average / HOURS_PER_DAY, 1)} ${t(translations.days)}`),
              },
              ...sectionsMeanDays.map((val) => ({ value: val })),
            ],
          },
          {
            cells: [
              { value: '' },
              { value: '' },
              { value: tableSubHeaderName(primaryCurrency) },
              ...sectionList.map(() => ({
                value: tableSubHeaderName(primaryCurrency),
              })),
            ],
          },
        ];
      }
      default:
        return [];
    }
  };

  const tableRowName = (name) => {
    return <span className="table-row-name">{name}</span>;
  };

  const tableSubHeaderName = (name) => {
    return <span className="table-subheader-name">{name}</span>;
  };

  const generateTableAlignment = (selectedType) => {
    let columnAlignment = [];

    if (selectedType) {
      switch (selectedType) {
        case simulationChartType.ACTIVITY_DURATIONS:
          columnAlignment = ['left', 'right', 'right', 'right', 'right'];
          break;
        case simulationChartType.RISKS:
          columnAlignment = ['left', 'right', 'right'];
          break;
        case simulationChartType.COST_ITEM_OVERVIEW:
          columnAlignment = ['left', 'left', 'right', 'right', 'right', 'right'];
          break;
        case simulationChartType.OPERATION_DURATIONS:
        case simulationChartType.SECTION_DURATIONS:
          columnAlignment = ['left', 'right', 'right', 'right', 'right'];
          break;
        case simulationChartType.TIME_DEPTH:
          columnAlignment = ['right', 'right', 'right', 'right', 'right', 'right', 'right'];
          break;
        case simulationChartType.CUMULATIVE_PROBABILITY:
          columnAlignment = [
            'left',
            'right',
            'right',
            'right',
            'right',
            'right',
            'right',
            'right',
            'right',
            'right',
            'right',
            'right',
          ];
          break;
        case simulationChartType.AFE_REPORT: {
          const sectionCount = (tableData?.sectionList ?? []).length;
          columnAlignment = ['center', 'left', 'right', ...Array(sectionCount).fill('right')];
          break;
        }
        default:
      }
    }

    return columnAlignment;
  };

  const generateTableHeaderAlignment = (selectedType) => {
    let columnAlignment = [];

    if (selectedType) {
      switch (selectedType) {
        case simulationChartType.TIME_DEPTH:
          columnAlignment = ['center', 'center', 'center', 'center', 'center', 'center', 'center'];
          break;

        default:
      }
    }
    return [columnAlignment, columnAlignment];
  };

  const generateRows = (tableData, selectedType) => {
    let result = [];
    const createCostRow = (item, depth) => {
      const rowStyle = {
        style: {
          backgroundColor: item?.children ? 'var(--color-background)' : '',
        },
      };

      result.push({
        onRowClick: () => toggleExpandedRow(item.id),
        cells: [
          {
            ...rowStyle,
            value: costCodesIdToCodeMap.get(item?.cost_code_id),
          },
          {
            ...rowStyle,
            value: (
              <>
                <Spacer width={21 * depth} />
                {item?.children && (
                  <>
                    <Icon icon={closedIds.includes(item.id) ? 'folder' : <TbFolderOpen />} />
                    <Spacer width="8px" />
                  </>
                )}
                <Text>{item.name}</Text>
              </>
            ),
          },
          {
            ...rowStyle,
            value: getNumberValue(item.results[0]),
          },
          {
            ...rowStyle,
            value: getNumberValue(item.results[1]),
          },
          {
            ...rowStyle,
            value: getNumberValue(item.results[2]),
          },
          {
            ...rowStyle,
            value: getNumberValue(item.results[3]),
          },
        ],
      });

      if (item.children && item.children.length > 0) {
        if (!closedIds.includes(item.id)) {
          item.children.forEach((item) => {
            createCostRow(item, depth + 1);
          });
        }
      }
    };

    if (tableData) {
      switch (selectedType) {
        case simulationChartType.OPERATION_DURATIONS:
        case simulationChartType.SECTION_DURATIONS:
          result = tableData?.map((el) => {
            if (selectedDataEntity === 0) {
              return {
                cells: [
                  { value: tableRowName(el.label) },
                  { value: roundToFixed(el.mean, 2) },
                  { value: roundToFixed(el.P10, 2) },
                  { value: roundToFixed(el.P50, 2) },
                  { value: roundToFixed(el.P90, 2) },
                ],
              };
            }
            return {
              cells: [
                { value: tableRowName(el.label) },
                {
                  value: displayNumber(round(el.mean, 0), {
                    scientific: false,
                  }),
                },
                {
                  value: displayNumber(round(el.P10, 0), {
                    scientific: false,
                  }),
                },
                {
                  value: displayNumber(round(el.P50, 0), {
                    scientific: false,
                  }),
                },
                {
                  value: displayNumber(round(el.P90, 0), {
                    scientific: false,
                  }),
                },
              ],
            };
          });
          break;

        case simulationChartType.CUMULATIVE_PROBABILITY:
          result = [
            {
              cells: [
                {
                  value: t(
                    selectedDataEntity === 0
                      ? translations.simulations_estimatedTime
                      : translations.simulations_estimatedCost,
                  ),
                },
                ...tableData.map((el) => {
                  if (selectedDataEntity === 0) {
                    return { value: round(el, 2) };
                  }
                  return {
                    value: displayNumber(round(el, 0), {
                      scientific: false,
                    }),
                  };
                }),
              ],
            },
            selectedDataEntity === 0 &&
              subactivity && {
                cells: [
                  { value: t(translations.simulations_riskOnly) },
                  ...subactivity?.map((el) => ({ value: round(el, 2) })),
                ],
              },
          ].filter(Boolean);
          break;
        case simulationChartType.TIME_DEPTH:
          result = tableData?.['10']?.depth?.map((el, index) => {
            const groupedSections = groupBy(
              tableData.sections?.filter((s) => s.projectSectionId != null),
              'projectSectionId',
            );
            const currentSectionId = tableData?.sections?.[index]?.projectSectionId;
            const previousSectionId = tableData?.sections?.[index - 1]?.projectSectionId;
            const hasSectionData = currentSectionId != null;
            const hadPreviousSectionData = previousSectionId != null;

            const isFirstInSection = hasSectionData && (index === 0 || currentSectionId !== previousSectionId);

            const isFirstWithoutSection = !hasSectionData && (index === 0 || hadPreviousSectionData);

            const rowsWithoutSection = tableData.sections
              ?.slice(index)
              .filter((s) => s.projectSectionId == null).length;

            const getSectionCells = () => {
              if (!showSections) return [];
              if (isFirstInSection) {
                return [
                  {
                    value: (
                      <CellWithExpandingIcon
                        cellContent={tableData?.sections?.[index]?.projectSectionName}
                        textAlign="left"
                      />
                    ),
                    rowSpan: groupedSections?.[currentSectionId]?.length,
                  },
                ];
              }
              if (isFirstWithoutSection) {
                return [
                  {
                    value: '',
                    rowSpan: rowsWithoutSection,
                  },
                ];
              }
              return [];
            };

            const sectionCells = getSectionCells();

            return {
              cells: [
                ...sectionCells,
                ...(index % 2 === 0
                  ? [
                      {
                        value: (
                          <CellWithExpandingIcon cellContent={tableData?.operations?.[index]?.name} textAlign="left" />
                        ),
                        rowSpan: 2,
                        style: { borderLeftWidth: 1 },
                      },
                    ]
                  : []),
                {
                  value: tableRowName(round(unum(el, depthUnit, 'm'), 0)),
                  style: { borderLeftWidth: 1 },
                },

                {
                  value:
                    selectedDataEntity === 0
                      ? roundToFixed(tableData?.mean?.time?.[index], 2)
                      : displayNumber(round(tableData?.mean?.costs?.[index], 0), {
                          scientific: false,
                        }),
                },

                {
                  value:
                    selectedDataEntity === 0
                      ? roundToFixed(tableData?.['10']?.time?.[index], 2)
                      : displayNumber(round(tableData?.['10']?.costs?.[index], 0), {
                          scientific: false,
                        }),
                },
                {
                  value:
                    selectedDataEntity === 0
                      ? roundToFixed(tableData?.['50']?.time?.[index], 2)
                      : displayNumber(round(tableData?.['50']?.costs?.[index], 0), {
                          scientific: false,
                        }),
                },
                {
                  value:
                    selectedDataEntity === 0
                      ? roundToFixed(tableData?.['90']?.time?.[index], 2)
                      : displayNumber(round(tableData?.['90']?.costs?.[index], 0), {
                          scientific: false,
                        }),
                },
              ],
            };
          });
          break;

        case simulationChartType.ACTIVITY_DURATIONS:
          result = tableData
            ?.map((el) => {
              return el.activity.map((activity) => {
                const cells = [
                  {
                    value: activity.name,
                  },
                  { value: roundToFixed(activity.average, 2) },
                  { value: roundToFixed(activity.percentiles[0], 2) },
                  { value: roundToFixed(activity.percentiles[1], 2) },
                  { value: roundToFixed(activity.percentiles[2], 2) },
                ];
                return { cells };
              });
            })
            .flat();
          break;
        case simulationChartType.COST_ITEM_OVERVIEW:
          tableData?.forEach((item) => {
            createCostRow(item, 0);
          });
          result.push(getCostItemOverviewTotal(simulationsResult));
          break;
        case simulationChartType.AFE_REPORT: {
          const {
            costResults: afeItems = [],
            sectionList = [],
            sectionContributionToCost = {},
            sectionCostPerItem = {},
            sectionCosts: afeSectionCosts = { labels: [], costs: { mean: [] } },
            average_cost: afeAverageCost,
          } = tableData || {};

          const getSectionCostsForItem = (costId) =>
            sectionList.map((sectionName) => {
              const contributing = sectionContributionToCost[costId];
              const val = round(contributing?.find((s) => s.name === sectionName)?.mean, 0);
              return { value: val ? thousandsSeparators(val) : '' };
            });

          const collectCategoryIds = (tree) =>
            tree.reduce((acc, node) => acc.concat(node.id, node.children ? collectCategoryIds(node.children) : []), []);

          const createAfeRow = (item, depth) => {
            const isCategory = item.children && item.children.length > 0;
            const rowStyle = {
              style: {
                backgroundColor: isCategory ? 'var(--color-background)' : '',
              },
            };

            let sectionCells = getSectionCostsForItem(item.id);

            if (isCategory && depth === 0) {
              const categoryIds = collectCategoryIds(item.children);
              sectionCells = sectionList.map((sectionName) => {
                const total = (sectionCostPerItem[sectionName] || []).reduce(
                  (sum, cost) => (categoryIds.includes(cost.id) ? sum + cost.mean : sum),
                  0,
                );
                return {
                  ...rowStyle,
                  value: thousandsSeparators(round(total, 0)),
                };
              });
            } else {
              sectionCells = sectionCells.map((c) => ({ ...rowStyle, ...c }));
            }

            result.push({
              onRowClick: () => toggleExpandedRow(item.id),
              cells: [
                {
                  ...rowStyle,
                  value: costCodesIdToCodeMap.get(item?.cost_code_id),
                },
                {
                  ...rowStyle,
                  value: (
                    <>
                      <Spacer width={21 * depth} />
                      {isCategory && (
                        <>
                          <Icon icon={closedIds.includes(item.id) ? 'folder' : <TbFolderOpen />} />
                          <Spacer width="8px" />
                        </>
                      )}
                      <Text>{item.name}</Text>
                    </>
                  ),
                },
                {
                  ...rowStyle,
                  value: thousandsSeparators(round(item.results[0], 0)),
                },
                ...sectionCells,
              ],
            });

            if (isCategory && !closedIds.includes(item.id)) {
              item.children.forEach((child) => createAfeRow(child, depth + 1));
            }
          };

          afeItems.forEach((item) => createAfeRow(item, 0));

          const afeSectionTotals = sectionList.map((sectionName) => {
            const sectionIdx = afeSectionCosts.labels.findIndex((s) => s === sectionName);
            return {
              value: thousandsSeparators(round(afeSectionCosts.costs.mean[sectionIdx], 0)),
            };
          });

          result.push({
            cells: [
              {
                value: '',
                style: {
                  backgroundColor: 'var(--color-background)',
                  fontWeight: 'bold',
                },
              },
              {
                value: t(translations.total),
                style: {
                  backgroundColor: 'var(--color-background)',
                  fontWeight: 'bold',
                },
              },
              {
                value: thousandsSeparators(round(afeAverageCost, 0)),
                style: {
                  backgroundColor: 'var(--color-background)',
                  fontWeight: 'bold',
                },
              },
              ...afeSectionTotals.map((c) => ({
                ...c,
                style: {
                  backgroundColor: 'var(--color-background)',
                  fontWeight: 'bold',
                },
              })),
            ],
          });
          break;
        }
        case simulationChartType.RISKS:
          result = tableData?.map((row) => ({
            cells: [
              {
                value: row.name,
              },
              {
                value: round(row.certainty),
              },
              {
                value: roundToFixed(row.mean, 2),
              },
            ],
          }));
          break;
        case simulationChartType.SECTION_WATERFALL: {
          const { dataType, sections, values, totalSum, threshold } = getSimulationDataForWaterfall(
            simulationsResult,
            selectedDataEntity,
            HOURS_PER_DAY,
          );
          const { labels = [] } = sections || {};

          result = [...labels, t(translations.total)]?.map((row, index) => ({
            cells: [
              {
                value: row,
              },
              {
                value:
                  dataType === sectionObjectKeys.DURATIONS
                    ? roundToFixed([...values, totalSum][index], 2)
                    : roundToFixed(normalizeValue([...values, totalSum][index], threshold), 2),
              },
            ],
          }));
          break;
        }
        default:
          result = [];
          break;
      }
    }
    return result;
  };

  const extractCellValue = (cell) => {
    if (cell == null || cell.value == null) return '';
    const { value: val } = cell;

    if (typeof val !== 'object') return val;

    if (val.props != null) {
      if (val.props.cellContent !== undefined) {
        return val.props.cellContent ?? '';
      }

      const { children } = val.props;

      if (children === undefined || children === null) return '';

      if (typeof children === 'string' || typeof children === 'number') {
        return children;
      }

      if (Array.isArray(children)) {
        const found = children.find((child) => {
          if (child == null || child === false || child === true) return false;
          if (typeof child === 'string' || typeof child === 'number') return true;
          if (child.props?.children !== undefined) {
            const nested = extractCellValue({ value: child });
            return nested !== '' && nested != null;
          }
          return false;
        });

        if (found == null) return '';
        if (typeof found === 'string' || typeof found === 'number') return found;
        return extractCellValue({ value: found });
      }

      return extractCellValue({ value: children });
    }

    return String(val);
  };

  //Converts extracted cell value to a proper Excel value
  const toExcelValue = (raw) => {
    if (raw == null || raw === '') return '';
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/[\s\u00A0\u2009]/g, '').replace(/,/g, '');
      const num = Number(cleaned);
      if (!Number.isNaN(num) && cleaned !== '') return num;
    }
    return raw;
  };

  const getColumnFormats = (selectedType) => {
    const spaceThousands = (decimals) => {
      const intPart = '#\\ ###\\ ###\\ ##0';
      return decimals > 0 ? `${intPart}.${'0'.repeat(decimals)}` : intPart;
    };

    const currencyFormat = spaceThousands(0);
    const currencyDecimalFormat = spaceThousands(2);
    const daysFormat = spaceThousands(2);
    const hoursFormat = spaceThousands(2);
    const percentFormat = '0"%"';
    const depthFormat = spaceThousands(0);

    const isCost = selectedDataEntity !== 0;
    const valueFormat = isCost ? currencyFormat : daysFormat;

    switch (selectedType) {
      case simulationChartType.OPERATION_DURATIONS:
      case simulationChartType.SECTION_DURATIONS:
        // [1:Operation, 2:Mean, 3:P10, 4:P50, 5:P90]
        return {
          2: valueFormat,
          3: valueFormat,
          4: valueFormat,
          5: valueFormat,
        };

      case simulationChartType.TIME_DEPTH: {
        // With sections: [1:Section, 2:Operation, 3:Depth, 4:Mean, 5:P10, 6:P50, 7:P90]
        // Without:       [1:Operation, 2:Depth, 3:Mean, 4:P10, 5:P50, 6:P90]
        const offset = showSections ? 0 : -1;
        return {
          [3 + offset]: depthFormat,
          [4 + offset]: valueFormat,
          [5 + offset]: valueFormat,
          [6 + offset]: valueFormat,
          [7 + offset]: valueFormat,
        };
      }

      case simulationChartType.CUMULATIVE_PROBABILITY:
        // [1:Label, 2:P1, 3:P10, ...]
        return shownCumulativeProbabilities.reduce(
          (acc, _, i) => ({
            ...acc,
            [i + 2]: valueFormat,
          }),
          {},
        );

      case simulationChartType.ACTIVITY_DURATIONS:
        // [1:Activity, 2:Mean, 3:P10, 4:P50, 5:P90]
        return {
          2: hoursFormat,
          3: hoursFormat,
          4: hoursFormat,
          5: hoursFormat,
        };

      case simulationChartType.COST_ITEM_OVERVIEW:
        // [1:Code, 2:Name, 3:Mean, 4:P10, 5:P50, 6:P90]
        return {
          3: currencyDecimalFormat,
          4: currencyDecimalFormat,
          5: currencyDecimalFormat,
          6: currencyDecimalFormat,
        };

      case simulationChartType.RISKS:
        // [1:Risk, 2:Probability, 3:MeanTime]
        return { 2: percentFormat, 3: hoursFormat };

      case simulationChartType.SECTION_WATERFALL:
        // [1:Section, 2:Value]
        return { 2: isCost ? currencyFormat : daysFormat };

      case simulationChartType.AFE_REPORT: {
        // [1:Code, 2:Name, 3:Total, 4..N:Sections]
        const sectionCount = (tableData?.sectionList ?? []).length;
        const formats = { 3: currencyFormat };
        for (let i = 0; i < sectionCount; i += 1) {
          formats[4 + i] = currencyFormat;
        }
        return formats;
      }

      default:
        return {};
    }
  };

  const buildGrid = (exportData) => {
    const { headers, rows } = exportData;
    const numCols = headers?.[0]?.cells?.length || 0;

    const headerGrid = (headers || []).map((headerRow) => {
      const cells = headerRow.cells || [];
      return cells.map((cell) => {
        const val = extractCellValue(cell);
        return typeof val === 'object' ? '' : (val ?? '');
      });
    });

    const occupiedUntil = new Array(numCols).fill(0);
    const dataGrid = [];

    (rows || []).forEach((row) => {
      const gridRowIdx = dataGrid.length;
      const gridRow = new Array(numCols).fill('');
      dataGrid.push(gridRow);

      let cellIdx = 0;
      for (let colIdx = 0; colIdx < numCols; colIdx += 1) {
        if (occupiedUntil[colIdx] <= gridRowIdx && cellIdx < (row.cells?.length || 0)) {
          const cell = row.cells[cellIdx];
          const rawVal = extractCellValue(cell);
          gridRow[colIdx] = rawVal;

          const rowSpan = cell.rowSpan || 1;
          if (rowSpan > 1) {
            occupiedUntil[colIdx] = gridRowIdx + rowSpan;
          }

          cellIdx += 1;
        }
      }
    });

    return { headerGrid, dataGrid };
  };

  const computeMerges = (sourceRows, numCols, headerOffset) => {
    const occupiedUntil = new Array(numCols).fill(0);
    const merges = [];

    sourceRows.forEach((row, rowIndex) => {
      let cellIdx = 0;
      for (let colIdx = 0; colIdx < numCols; colIdx += 1) {
        if (occupiedUntil[colIdx] <= rowIndex && cellIdx < (row.cells?.length || 0)) {
          const cell = row.cells[cellIdx];
          const rowSpan = cell.rowSpan || 1;
          if (rowSpan > 1) {
            occupiedUntil[colIdx] = rowIndex + rowSpan;
            merges.push({
              startRow: rowIndex + headerOffset + 1,
              startCol: colIdx + 1,
              endRow: rowIndex + rowSpan - 1 + headerOffset + 1,
              endCol: colIdx + 1,
            });
          }
          cellIdx += 1;
        }
      }
    });

    return merges;
  };

  const onClickDownLoad = (exportData) => {
    try {
      const { headers } = exportData;
      const { headerGrid, dataGrid } = buildGrid(exportData);

      const workBook = new ExcelJS.Workbook();
      const rawTitle = selectedItem?.value || 'Export';
      const sheetTitle = rawTitle.replace(/[*?:\\/[\]]/g, '-').slice(0, 31);
      const workSheet = workBook.addWorksheet(sheetTitle);

      const headerOffset = headerGrid.length;
      const columnFormats = getColumnFormats(selectedItem?.value);

      //header rows
      headerGrid.forEach((row) => {
        const excelRow = workSheet.addRow(row);
        excelRow.font = { bold: true };
      });

      const numCols = headers?.[0]?.cells?.length || 0;
      const sourceRows = exportData.rows || [];
      const merges = computeMerges(sourceRows, numCols, headerOffset);

      //data rows converting to Excel values and applying number formats
      dataGrid.forEach((gridRow) => {
        const excelRow = workSheet.addRow(gridRow.map((v) => toExcelValue(v)));
        excelRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          const fmt = columnFormats[colNumber];
          if (fmt && typeof cell.value === 'number') {
            cell.numFmt = fmt;
          }
        });
      });

      merges.forEach((m) => {
        try {
          workSheet.mergeCells(m.startRow, m.startCol, m.endRow, m.endCol);
        } catch (e) {
          console.log(e);
        }
      });

      //column widths
      workSheet.columns.forEach((column) => {
        let maxLength = 10;
        column.eachCell?.({ includeEmpty: true }, (cell) => {
          const cellLength = cell.value ? String(cell.value).length : 0;
          if (cellLength > maxLength) {
            maxLength = cellLength;
          }
        });
        column.width = Math.min(maxLength + 2, 40);
      });

      writeExcelFile(workBook, sheetTitle);
    } catch (error) {
      toast({
        message: {
          type: 'Error',
          content: t(translations.unableToDownloadExcel),
          details: error?.message || String(error),
        },
      });
    }
  };

  const onClickCopyClipboard = (exportData, separator) => {
    try {
      const { headerGrid, dataGrid } = buildGrid(exportData);

      const formatValue = (val, isHeader) => {
        if (val == null || val === '') return '';
        if (isHeader) return String(val);

        const str = String(val);

        const cleaned = str.replace(/[\s\u00A0\u2009]/g, '').replace(/,/g, '');
        const num = Number(cleaned);
        const isNumeric = !Number.isNaN(num) && cleaned !== '';

        if (isNumeric) {
          const numStr = String(num);
          if (separator === decimalSeparator.COMMA) {
            return numStr.replace(/\./g, ',');
          }
          return numStr;
        }

        return str;
      };

      const allRows = [
        ...headerGrid.map((row) => row.map((v) => formatValue(v, true)).join('\t')),
        ...dataGrid.map((row) => row.map((v) => formatValue(v, false)).join('\t')),
      ];

      const text = allRows.join('\n');

      navigator.clipboard.writeText(text).then(() => {
        toast({
          message: {
            type: 'Success',
            content: t(translations.copiedToClipboard),
          },
        });
      });
    } catch (error) {
      toast({
        message: {
          type: 'Error',
          content: t(translations.unableToCopyToClipboard),
          details: error?.message || String(error),
        },
      });
    }
  };

  const testIdTable = visibleEstimates[chartKey]?.name
    ? `table-result-${convertToKebabCase(visibleEstimates[chartKey]?.name)}`
    : 'table-result';

  const table = {
    testId: testIdTable,
    columnAlignment: generateTableAlignment(selectedItem?.value),
    columnHeaderAlignments: generateTableHeaderAlignment(selectedItem?.value),
    headers: generateTableHeaders(selectedItem?.value),
    rows: generateRows(tableData, selectedItem?.value),
    striped: selectedItem?.value !== simulationChartType.COST_ITEM_OVERVIEW,
  };

  const exportOptions = [
    {
      type: 'Option',
      label: t(translations.downloadFile),
      onClick: () => {
        if (selectedItem?.value === simulationChartType.AFE_REPORT) {
          downloadAFEReportExcel(simulationsResult, costCodesIdToCodeMap, primaryCurrency, name, wellbore?.name);
        } else {
          onClickDownLoad(table);
        }
      },
    },
    {
      type: 'Menu',
      menu: {
        trigger: 'Text',
        label: t(translations.copyToClipboard),
        sections: [
          {
            type: 'Option',
            label: t(translations.copyDecimalDot),
            onClick: () => onClickCopyClipboard(table, decimalSeparator.DOT),
          },
          {
            type: 'Option',
            label: t(translations.copyDecimalComma),
            onClick: () => onClickCopyClipboard(table, decimalSeparator.COMMA),
          },
        ],
      },
    },
  ];

  if (!tableData || (Array.isArray(tableData) && !tableData.length)) {
    return (
      <Flex alignItems="center" justifyContent="center" height="100%">
        <Heading top>There is no data for {selectedItem?.label}</Heading>
      </Flex>
    );
  }
  return (
    <>
      <Portal id={controlsPortalId}>
        <Menu
          menu={{
            component: <Button basic icon={<TbDownload />} colored="muted" round small />,
            trigger: 'Component',
            small: true,
            sections: exportOptions,
          }}
        />
      </Portal>
      <div id={controlsPortalId} />
      <Spacer height="12px" />
      <Table table={table} />
    </>
  );
};

const mapStateToProps = (state) => {
  const primaryCurrency = selectPrimaryCurrency(state.entities);
  const costCodesIdToCodeMap = selectCostCodesIdToCodeMap({
    entities: state.entities,
  });
  const {
    activityModel: { operations: activityModelOperations },
  } = state.entities;
  const activeEstimates = selectCompareEstimatesActiveEstimates(state);
  const visibleEstimates = activeEstimates.filter((item) => item?.visible !== false);

  return {
    depthUnit: selectUnitByQuantity(state.entities, QuantitiesTypes.Depth),
    primaryCurrency,
    activityModelOperations,
    costCodesIdToCodeMap,
    visibleEstimates,
    projectOverview: selectProjectOverview(state.entities),
  };
};

export default withErrorBoundary(connect(mapStateToProps)(ResultTable));
